import { CategoryDetail } from "../../types/categoryDetail";
import type { EntityDetailMode } from "../../types/entityRegistry";
import { ProductCard } from "../../types/productPlane";
import type { DetailSection } from "../Detail/DetailSections";
import SourceTable from "../Detail/SourceTable";
import {
  CategoryOverview,
  CategoryProducts,
  NarrowerClasses,
  SpecificationRanges,
  TypicalCapabilities,
} from "./CategoryBlocks";

// A count for the strip, where the section holds anything to count
const countOf = (count: number): number | undefined =>
  count > 0 ? count : undefined;

export interface CategorySectionActions {
  // Asks for the whole list of typical capabilities
  showAllCapabilities: () => void;
  // Asks for the whole list of specification ranges
  showAllSpecifications: () => void;
  // Turns the product list to the page that starts at the offset
  pageProducts: (offset: number) => void;
  // Opens a product
  openProduct: (product: ProductCard) => void;
}

/**
 * The sections of the category view, in the order shown. A section the
 * category has nothing in has no content, and so is left out of the view.
 * The overview always says what kind of class it is.
 */
export const categorySections = (
  category: CategoryDetail,
  mode: EntityDetailMode,
  actions: CategorySectionActions,
): DetailSection[] => [
  {
    id: "overview",
    label: "Overview",
    content: <CategoryOverview category={category} full />,
  },
  {
    id: "narrower",
    label: "Narrower classes",
    count: countOf(category.narrower.total),
    content: category.narrower.items.length > 0 && (
      <NarrowerClasses rows={category.narrower.items} />
    ),
  },
  {
    // The list heads itself: its line holds "Show all"
    id: "capabilities",
    label: "Typical capabilities",
    hideHeading: true,
    count: countOf(category.capabilities.total),
    content: category.capabilities.items.length > 0 && (
      <TypicalCapabilities
        page={category.capabilities}
        onShowAll={actions.showAllCapabilities}
      />
    ),
  },
  {
    id: "specifications",
    label: "Specification ranges",
    hideHeading: true,
    count: countOf(category.specifications.total),
    content: category.specifications.items.length > 0 && (
      <SpecificationRanges
        categoryId={category.id}
        page={category.specifications}
        onShowAll={actions.showAllSpecifications}
      />
    ),
  },
  {
    id: "products",
    label: "Products",
    count: countOf(category.products.total),
    content: category.products.items.length > 0 && (
      <CategoryProducts
        page={category.products}
        onOpen={actions.openProduct}
        onPage={actions.pageProducts}
      />
    ),
  },
  {
    id: "sources",
    label: "Sources",
    count: countOf(category.source_count),
    content: category.sources.items.length > 0 && (
      <SourceTable
        sources={category.sources.items}
        total={category.sources.total}
        mode={mode}
      />
    ),
  },
];
